import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { batch, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { createStyles, alpha, Theme } from '@material-ui/core/styles';

import { DialogComponent, TextField } from 'common/components';
import NotifyVariant from 'modules/booking/components/Modals/NotifyVariant';

import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';

import { EventUpdateType, IEventAction } from 'modules/booking/interfaces';

import { initialValuesEventAction } from 'modules/booking/components/Modals/initialValues';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';
import { EShortNotificationType } from 'modules/booking/constants/notificationType';

import { resetBookingEvents } from 'modules/booking/state/senderAvailability/actions';

import messages from 'modules/booking/messages';
import inputErrors from 'common/messages/inputErrors';
import { selectSenderAvailability } from 'modules/booking/state/senderAvailability/selectors';
import useRootSelector from 'common/hooks/useRootSelector';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        maxWidth: 434,
      },
    },
    warningBox: {
      padding: '12px',
      borderRadius: '3px',
      color: theme.palette.warning.main,
      backgroundColor: alpha(theme.palette.warning.main, 0.08),
    },
    selectInputRow: {
      minWidth: 112,
      maxWidth: 300,
      flex: 1,
    },
    selectInput: {
      '& .MuiOutlinedInput-input': {
        padding: '10px 12px',
      },
    },
  }),
);

const DeleteEventValidationSchema = yup.object().shape({
  reason: yup.string().max(1000, () => inputErrors.reasonLengthError),
});

interface IDeleteEventModalProps {
  isOpen: boolean;
  isUpdateEventLoading?: boolean;
  onClose: () => void;
  onSubmit: (formValues: IEventAction) => void;
  type: SenderAvailabilityTypeList;
  onLoadSenderAvailability?: (
    type: SenderAvailabilityTypeList,
    shortTypeEvent: EShortNotificationType,
  ) => void;
  isRepeatedEvent?: boolean;
}

const DeleteEventModal = ({
  isOpen,
  isUpdateEventLoading,
  onClose,
  onSubmit,
  type,
  onLoadSenderAvailability,
  isRepeatedEvent,
}: IDeleteEventModalProps): JSX.Element => {
  const senderAvailability = useRootSelector(selectSenderAvailability(type));

  const formMethods = useForm<IEventAction>({
    defaultValues: initialValuesEventAction,
    resolver: yupResolver(DeleteEventValidationSchema) as any, // TODO - PRM-1810 need resolver type
    mode: 'onBlur',
  });

  const classes = useStyles();
  const dispatch = useDispatch();
  const renderErrorMessage = useRenderIntlMessage();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const handleDeleteEvent = (formData: IEventAction) => {
    onSubmit(formData);
  };

  useEffect(() => {
    if (isOpen && onLoadSenderAvailability) {
      onLoadSenderAvailability(type, EShortNotificationType.Delete);
    }
    return () => {
      batch(() => {
        dispatch(resetBookingEvents({ type }));
      });
    };
  }, [dispatch, isOpen, onLoadSenderAvailability, type]);

  return (
    <DialogComponent
      className={classes.dialog}
      title={<FormattedMessage {...messages.deleteEventTitle} />}
      submitBtnTitle={<FormattedMessage {...messages.deleteEventBtn} />}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit(handleDeleteEvent)}
      loading={isUpdateEventLoading}
    >
      <FormProvider {...formMethods}>
        <form id="delete-event-form" autoComplete="none">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className={classes.warningBox} display="flex" alignItems="center">
                <Typography>
                  <FormattedMessage {...messages.deleteEventWarning} />
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="updateType"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="recurringEventUpdateType"
                      name="eventUpdateType"
                      value={value}
                      onBlur={onBlur}
                      onChange={onChange}
                    >
                      {isRepeatedEvent && (
                        <>
                          <FormControlLabel
                            value={EventUpdateType.OnlyCurrentInstance}
                            control={<Radio />}
                            label={<FormattedMessage {...messages.editRecurringThisEventOption} />}
                          />
                          <FormControlLabel
                            value={EventUpdateType.CurrentAndAllNext}
                            control={<Radio />}
                            label={
                              <FormattedMessage {...messages.editRecurringFutureEventOption} />
                            }
                          />
                          <FormControlLabel
                            value={EventUpdateType.All}
                            control={<Radio />}
                            label={<FormattedMessage {...messages.editRecurringAllEventOption} />}
                          />
                        </>
                      )}
                    </RadioGroup>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box mt={1}>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={<FormattedMessage {...messages.deleteEventReasonLabel} />}
                      multiline
                      rowsMax={3}
                      autoComplete="none"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.reason}
                      helperText={renderErrorMessage(errors?.reason?.message)}
                    />
                  )}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                <Grid item xs={12}>
                  <NotifyVariant senderAvailability={senderAvailability} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </DialogComponent>
  );
};

export default DeleteEventModal;
